import { useRef, useState } from "react";
import { supabase } from "../supabase";
import Toast from "./Toast";
import { createNotification } from "../utils/notifications";
import { PiHeartFill, PiHeartBold } from "react-icons/pi";

// Controlled component: the parent page owns `likes`/`liked` (as part of
// its `designs` state) and keeps them current via ONE shared realtime setup
// for the whole page (the designs UPDATE channel for `likes`, plus
// utils/myEngagement.js for whether the current viewer liked it), rather
// than every LikeButton on screen opening its own subscription. Call
// `onUpdate` after a successful write so the parent's own state (and this
// button) reflects it immediately.
export default function LikeButton({ designId, likes, liked, user, designOwnerId, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  // disabled={loading} only takes effect on the next render — a very fast
  // double-tap can still fire a second handleLike before that paints,
  // toggling the like twice (cancelling out a single tap) and burning two
  // calls against the rate limit for one action (same class of bug guarded
  // elsewhere, e.g. DownloadButton.jsx).
  const actionRef = useRef(false);

  async function handleLike() {
    if (!user) {
      setToast("Please log in to like");
      return;
    }

    if (loading || actionRef.current) return;
    actionRef.current = true;

    setLoading(true);

    // Atomic RPC (see utils/likes.js for why) — a plain read-then-write here
    // would race against another liker on the same design, or against the
    // ImageCarousel double-tap gesture on this very card, and silently drop
    // one of the two likes.
    const { data, error } = await supabase
      .rpc("toggle_design_like", { target_design_id: designId })
      .single();

    if (error) {
      console.log(error);
      setToast("Couldn't update your like. Try again.");
      setLoading(false);
      actionRef.current = false;
      return;
    }

    onUpdate?.({ likes: data.like_count, _likedByMe: data.liked });
    setToast(data.liked ? "Liked" : "Like removed");

    if (data.liked) {
      createNotification({ userId: designOwnerId, actorId: user.id, type: "like", designId });
    }

    setLoading(false);
    actionRef.current = false;
  }

  return (
    <>
      <button
        onClick={handleLike}
        disabled={loading}
        style={{
          ...btn,
          opacity: loading ? 0.6 : 1,
          color: liked ? "var(--accent-danger-strong)" : "var(--text-primary)"
        }}
      >
        {liked ? <PiHeartFill size={27} /> : <PiHeartBold size={27} />}
        <span>{likes || 0}</span>
      </button>

      {toast && (
        <Toast
          text={toast}
          onClose={() => setToast("")}
        />
      )}
    </>
  );
}

const btn = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  height: 40,
  padding: "0 2px",
  borderRadius: 20,
  border: "none",
  background: "none",
  cursor: "pointer",
  fontWeight: 700,
  fontSize: 14,
};
